import { useCallback, useRef, useState } from 'react';

export interface UseVoiceRecorderResult {
  recording: boolean;
  duration_seconds: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
}

const MIME_TYPE = 'audio/webm;codecs=opus';

/** Thin MediaRecorder wrapper — start/stop/duration/resulting Blob. No upload logic here. */
export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [recording, setRecording] = useState(false);
  const [duration_seconds, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder_ref = useRef<MediaRecorder | null>(null);
  const chunks_ref = useRef<Blob[]>([]);
  const stream_ref = useRef<MediaStream | null>(null);
  const timer_ref = useRef<number | null>(null);
  const stop_resolve_ref = useRef<((blob: Blob | null) => void) | null>(null);

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    setDuration(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream_ref.current = stream;
      const mime = MediaRecorder.isTypeSupported(MIME_TYPE) ? MIME_TYPE : '';
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      chunks_ref.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks_ref.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks_ref.current, { type: recorder.mimeType || MIME_TYPE });
        stream_ref.current?.getTracks().forEach((t) => t.stop());
        stream_ref.current = null;
        stop_resolve_ref.current?.(blob);
        stop_resolve_ref.current = null;
      };
      recorder_ref.current = recorder;
      recorder.start();
      setRecording(true);
      timer_ref.current = window.setInterval(() => setDuration((d) => d + 1), 1000);
    } catch {
      setError('voice_mic_permission_denied');
    }
  }, []);

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (timer_ref.current !== null) {
        window.clearInterval(timer_ref.current);
        timer_ref.current = null;
      }
      setRecording(false);
      const recorder = recorder_ref.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      stop_resolve_ref.current = resolve;
      recorder.stop();
    });
  }, []);

  return { recording, duration_seconds, error, start, stop };
}
